// Pure, framework-free helpers backing the OptionAutocomplete bridge component.
// Kept out of the React component so they can be unit tested in the project's
// `node` Vitest environment (no jsdom / @testing-library — components cannot be
// rendered). These functions own OptionAutocomplete's filter / select / clear /
// reopen behaviour; the component is a thin wiring layer over them and the
// presentation-only `Autocomplete` primitive.


/**
 * Resolve the selected option object for a raw `value` (an id string). Matches by
 * String coercion so a numeric id seeded from the API equals its string option
 * `value`. Returns null when nothing matches (the "none selected" state).
 */
function findSelected(options, value) {
  return options.find((o) => String(o.value) === String(value)) ?? null;
}


/** The input label for the currently selected option ('' when none). */
function labelForSelected(selected) {
  return selected?.label ?? '';
}


/**
 * Client-side filter of `options` against the typed `query`. When `query` equals
 * the selected option's label (i.e. the user reopened the field without typing),
 * return the FULL list so every option is visible again. Otherwise do a
 * case-insensitive substring match on each option's label. A null/undefined
 * query is treated as empty.
 */
function filterOptions(options, query, selected) {
  const text = query ?? '';
  if (text === labelForSelected(selected)) {
    return options;
  }
  const needle = text.toLowerCase();
  return options.filter((o) => String(o.label ?? '').toLowerCase().includes(needle));
}


/** The value emitted to `onChange` when an option row is selected. */
function valueFromSelectedOption(option) {
  return option.value;
}


/** Whether clearing the input text should clear the selection (emit `onChange('')`). */
function shouldClear(text) {
  return text === '';
}


/**
 * Reconcile a blur (F7-UX): the field must never keep uncommitted free text. If
 * the typed `query` has diverged from the committed option's label, the input
 * reverts to that label (the placeholder/sentinel label when nothing is selected),
 * and the selection is explicitly cleared (emit '') when no valid value is
 * committed — so the required validator fires instead of the field silently
 * proceeding with a typeless value. When the query already equals the committed
 * label, `changed` is false so the component can skip a redundant state update.
 *
 * @param {{ query: string, selected: object|null, value: unknown }} state
 * @returns {{ changed: boolean, query: string, clear: boolean }}
 */
function resolveBlur({ query, selected, value }) {
  const committedLabel = labelForSelected(selected);
  if (query === committedLabel) {
    return { changed: false, query: committedLabel, clear: false };
  }
  return { changed: true, query: committedLabel, clear: !value };
}


export {
  filterOptions,
  findSelected,
  labelForSelected,
  resolveBlur,
  shouldClear,
  valueFromSelectedOption,
};
